import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ChevronDown, ChevronUp, Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react';

const STEP_LABELS = {
  queued: { label: 'Waiting to start', color: 'text-gray-400', step: 0 },
  converting: { label: 'Converting to text (1/10)', color: 'text-blue-400', step: 1 },
  detecting_language: { label: 'Detecting language (2/10)', color: 'text-blue-400', step: 2 },
  uploading: { label: 'Uploading content (3/10)', color: 'text-blue-400', step: 3 },
  getting_url: { label: 'Preparing for chunking (4/10)', color: 'text-blue-400', step: 4 },
  chunking: { label: 'Breaking into chunks (5/10)', color: 'text-blue-400', step: 5 },
  storing: { label: 'Saving to database (6/10)', color: 'text-blue-400', step: 6 },
  embedding: { label: 'Creating embeddings (7/10)', color: 'text-blue-400', step: 7 },
  indexing: { label: 'Building search index (8/10)', color: 'text-blue-400', step: 8 },
  post_processing: { label: 'Post-processing (9/10)', color: 'text-blue-400', step: 9 },
  enriching: { label: 'Extracting media (10/10)', color: 'text-blue-400', step: 10 },
  completed: { label: 'Completed', color: 'text-emerald-400', step: 10 },
  failed: { label: 'Failed', color: 'text-red-400', step: -1 },
  retrying: { label: 'Retrying', color: 'text-amber-400', step: -1 },
  cancelled: { label: 'Cancelled', color: 'text-zinc-400', step: -1 },
};

function TaskRow({ task }) {
  const info = STEP_LABELS[task.status] || STEP_LABELS.queued;
  const progress = task.status === 'completed' ? 100 :
    task.status === 'failed' ? 0 :
    Math.round(((task.current_step || 0) / (task.total_steps || 10)) * 100);

  // Heartbeat staleness detection (5 minutes = 300000ms)
  const heartbeatAge = task.heartbeat_at ? 
    (Date.now() - new Date(task.heartbeat_at).getTime()) : null;
  const isStale = heartbeatAge !== null && heartbeatAge > 300000 && 
    !['completed', 'failed', 'queued'].includes(task.status);

  return (
    <div data-testid={`task-row-${task.celery_task_id}`} className="flex items-center gap-3 py-1.5 px-2 text-xs border-b border-zinc-800/50 last:border-0">
      <div className="w-5 flex-shrink-0">
        {task.status === 'completed' && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
        {task.status === 'failed' && <XCircle className="w-4 h-4 text-red-400" />}
        {task.status === 'cancelled' && <XCircle className="w-4 h-4 text-zinc-500" />}
        {task.status === 'queued' && <Clock className="w-4 h-4 text-gray-500" />}
        {!['completed', 'failed', 'queued', 'cancelled'].includes(task.status) && <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />}
      </div>
      <div className="flex-1 min-w-0 truncate text-zinc-300" title={task.url}>
        {task.url?.replace(/^https?:\/\//, '').substring(0, 60)}
      </div>
      <div className={`w-56 text-right ${info.color}`}>
        {info.label}
        {task.status === 'completed' && task.chunks_created > 0 && ` (${task.chunks_created} chunks)`}
        {task.step_substatus && !['completed', 'failed'].includes(task.status) && (
          <span className="text-zinc-400 ml-1 text-[10px]">— {task.step_substatus}</span>
        )}
        {task.status === 'failed' && task.error_message && (
          <span className="text-red-300 ml-1" title={task.error_message}>
            : {task.error_message.substring(0, 40)}
          </span>
        )}
        {isStale && (
          <span className="text-orange-400 ml-1 text-[10px]" title={`No heartbeat for ${Math.round(heartbeatAge/60000)} minutes`}>
            (may be stuck)
          </span>
        )}
      </div>
      <div className="w-12">
        <div className="h-1.5 bg-zinc-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              task.status === 'completed' ? 'bg-emerald-500' :
              task.status === 'failed' ? 'bg-red-500' :
              'bg-blue-500'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
}

export function BatchImportStatus({ batchDetails, activeBatches, isPolling, onCancelAll }) {
  const [expanded, setExpanded] = useState({});
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const allBatchIds = [
    ...activeBatches.map(b => b.batch_id),
    ...Object.keys(batchDetails).filter(id => !activeBatches.find(b => b.batch_id === id)),
  ];

  const uniqueBatchIds = [...new Set(allBatchIds)];

  if (uniqueBatchIds.length === 0 && !isPolling) return null;

  return (
    <Card data-testid="batch-import-status-panel" className="border-zinc-700/50 bg-zinc-900/50 mt-4">
      <CardHeader className="py-3 px-4">
        <CardTitle className="text-sm flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            {isPolling && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400" />}
            Active Imports
            <span className="text-xs text-zinc-500 font-normal">
              {uniqueBatchIds.length} batch{uniqueBatchIds.length !== 1 ? 'es' : ''}
            </span>
          </span>
          {onCancelAll && (
            confirmingCancel ? (
              <span className="flex items-center gap-2">
                <span className="text-xs text-zinc-400 font-normal">Cancel everything?</span>
                <button
                  data-testid="confirm-cancel-all-btn"
                  disabled={cancelling}
                  onClick={async () => {
                    setCancelling(true);
                    try { await onCancelAll(); } finally { setCancelling(false); setConfirmingCancel(false); }
                  }}
                  className="text-xs px-2 py-1 rounded-md bg-red-600 hover:bg-red-500 text-white disabled:opacity-60 flex items-center gap-1"
                >
                  {cancelling ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                  {cancelling ? 'Cancelling…' : 'Yes, cancel all'}
                </button>
                <button
                  data-testid="dismiss-cancel-all-btn"
                  disabled={cancelling}
                  onClick={() => setConfirmingCancel(false)}
                  className="text-xs px-2 py-1 rounded-md bg-zinc-700 hover:bg-zinc-600 text-zinc-200"
                >
                  No
                </button>
              </span>
            ) : (
              <button
                data-testid="cancel-all-imports-btn"
                onClick={() => setConfirmingCancel(true)}
                className="text-xs px-2 py-1 rounded-md border border-red-500/40 text-red-400 hover:bg-red-500/10 flex items-center gap-1"
              >
                <XCircle className="w-3.5 h-3.5" />
                Cancel All
              </button>
            )
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-3 space-y-3">
        {uniqueBatchIds.map(batchId => {
          const detail = batchDetails[batchId];
          const isExpanded = expanded[batchId] !== false;

          if (!detail) return (
            <div key={batchId} className="text-xs text-zinc-500">Loading batch {batchId.substring(0, 8)}...</div>
          );

          const progressPct = detail.total > 0
            ? Math.round(((detail.completed + detail.failed + (detail.cancelled || 0)) / detail.total) * 100) : 0;
          const allDone = detail.completed + detail.failed + (detail.cancelled || 0) === detail.total && detail.total > 0;

          return (
            <div key={batchId} data-testid={`batch-card-${batchId}`} className="border border-zinc-700/30 rounded-lg overflow-hidden">
              <div
                className="flex items-center justify-between py-2 px-3 cursor-pointer hover:bg-zinc-800/30"
                onClick={() => setExpanded(p => ({ ...p, [batchId]: !isExpanded }))}
              >
                <div className="flex items-center gap-2 text-xs">
                  {allDone ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />}
                  <span className="text-zinc-300">
                    Batch {batchId.substring(0, 8)}: {detail.completed}/{detail.total} completed
                    {detail.failed > 0 && <span className="text-red-400 ml-1">, {detail.failed} failed</span>}
                    {detail.in_progress > 0 && <span className="text-blue-400 ml-1">, {detail.in_progress} in progress</span>}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-20 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${allDone ? 'bg-emerald-500' : 'bg-blue-500'}`}
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                  <span className="text-xs text-zinc-500 w-8 text-right">{progressPct}%</span>
                  {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
                </div>
              </div>
              {isExpanded && detail.tasks && (
                <div className="border-t border-zinc-800/50 max-h-60 overflow-y-auto">
                  {detail.tasks.map(task => (
                    <TaskRow key={task.celery_task_id} task={task} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
